import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  ArrowRight,
  Check,
  ChevronDown,
  Mail,
  Phone,
} from 'lucide-react'
import DemoRequestForm from '@/components/dcuc/DemoRequestForm'
import WebsiteChat from '@/components/dcuc/WebsiteChat'
import { trackDcuc, useDcucTracking } from '@/lib/dcuc-tracking'
import '@/styles/dcuc-landing.css'

/**
 * scamdefense.org/dcuc: landing page for the October 2026 DCUC newsletter ad.
 * Audience is credit union leadership, not members. Job: explain the program
 * and capture a demo request. Deliberately does NOT link to the member site
 * (DCUC wants the full resource seen through a licence, not given away).
 * Carries its own chrome (see __root.tsx). Demo requests go to Lee and to
 * Mavis at Lightbulb Press via granguard-api /public/partner-inquiry.
 */
export const Route = createFileRoute('/dcuc')({ component: DcucLandingPage })

const DEMO_EMAIL = 'info@lightbulbpress.com'
const DEMO_PHONE = '917-375-6640'
const DEMO_PHONE_HREF = 'tel:+19173756640'

const NAVY = '#001946'
const RED = '#c8102e'

const WHY = [
  {
    title: 'Build on your existing education',
    body: 'Give members a resource to return to after an alert, a workshop or a conversation at the branch.',
  },
  {
    title: 'Keep your reporting guidance close',
    body: 'Put your credit union’s contact details and approved reporting steps where members can find them.',
  },
  {
    title: 'Make it relevant to military life',
    body: 'Explain the scams that exploit moves, deployments, benefits and family relationships, under your credit union’s brand.',
  },
]

const MEMBER_FEATURES = [
  {
    title: 'Interactive lessons and quizzes',
    body: 'A six-chapter course in short screens. Members can pause anytime and pick up where they left off.',
  },
  {
    title: 'Real-life scam scenarios',
    body: 'Members practice spotting the red flags in realistic calls, texts and emails before they meet the real thing.',
  },
  {
    title: 'Scam trends targeting military families',
    body: 'Plain-language guidance on benefits impersonation, deployment romance scams, rental fraud and other military-related scams.',
  },
  {
    title: 'Practical checklists and resources',
    body: 'Clear reminders help members pause, verify independently and find the right next step when something feels suspicious.',
  },
]

const CUSTOMIZE = [
  'Your logo and colors',
  'Your web address',
  'Your contact details',
  'Your reporting guidance',
]

const STEPS = [
  {
    title: 'Discuss your pilot',
    body: 'Tell us about your audience and existing education. We discuss a branded pilot, program options and the member experience.',
  },
  {
    title: 'Make it your own',
    body: 'Agree your branding, contact details and reporting guidance. We handle the site setup and ongoing content updates.',
  },
  {
    title: 'Share one link',
    body: 'Your team chooses where to promote it: your website, member emails, branch materials or community sessions.',
  },
]

const FAQS = [
  {
    q: 'We already offer fraud education. How does this fit?',
    a: 'OnGuard can sit alongside your alerts, workshops and fraud controls. It gives members an ongoing learning resource and a place to find your reporting guidance. A demo helps you decide where it would add value to what you already offer.',
  },
  {
    q: 'Do members need to create an account?',
    a: 'No. Members open one link in any browser. There is no app to download and nothing to sign up for.',
  },
  {
    q: 'Does it connect to our core or member financial accounts?',
    a: 'No. The OnGuard learning resource does not connect to your core systems, bank accounts or transaction data. Ordinary website usage and enquiry information are covered by the privacy policy.',
  },
  {
    q: 'What does my team need to do?',
    a: 'Choose a program owner, approve your branding and reporting contacts, and decide how you will share it. We handle the agreed site setup and content updates.',
  },
  {
    q: 'Can we see it before we commit?',
    a: 'Yes. Request a guided demonstration or ask for program information. You can discuss the member experience, branding and scope before deciding.',
  },
  {
    q: 'What does it cost?',
    a: 'The branded program is quoted for your credit union. Ask for program details or a demo, and the team will confirm the scope and pricing before you decide. Sending an enquiry does not commit you to a purchase.',
  },
]

function OnGuardLogo() {
  return (
    <span className="inline-flex items-center gap-2.5">
      <span className="text-[22px] font-extrabold tracking-[-0.01em]" style={{ color: RED }}>
        <span style={{ color: '#2a56c6' }}>On</span>Guard
      </span>
      <span aria-hidden className="hidden h-[18px] w-px bg-slate-300 sm:block" />
      <span className="hidden text-[15px] font-medium text-slate-600 sm:inline">Scam Defense</span>
    </span>
  )
}

function Eyebrow({ children, light = false }: { children: string; light?: boolean }) {
  return (
    <p className="text-base font-semibold" style={{ color: light ? '#ff8a96' : RED }}>
      {children}
    </p>
  )
}

function DcucLandingPage() {
  const [chatOpen, setChatOpen] = useState(false)
  useDcucTracking()
  return (
    <div className="dcuc-landing min-h-screen">
      <a href="#program" className="dl-skip-link">Skip to the program</a>
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex min-h-18 max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <a href="#program" aria-label="OnGuard program overview"><OnGuardLogo /></a>
          <div className="flex items-center gap-5">
            <a href={DEMO_PHONE_HREF} onClick={() => trackDcuc('phone_click','contact')} className="hidden items-center gap-2 text-[15px] font-medium text-slate-600 lg:inline-flex">
              <Phone className="h-4 w-4" aria-hidden="true" />{DEMO_PHONE}
            </a>
            <a href="#demo" onClick={() => trackDcuc('pilot_click', 'header')} className="dl-button dl-button-primary">Request a pilot</a>
          </div>
        </div>
      </header>

      <main>
        <section id="program" className="dl-anchor mx-auto grid max-w-6xl items-center gap-8 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[1.12fr_1fr] lg:gap-10">
          <div>
            <Eyebrow>A DCUC member benefit</Eyebrow>
            <h1 className="dl-display mt-4 max-w-[18ch] text-balance text-[clamp(2.5rem,4.6vw,3.6rem)] font-bold leading-[1.06]" style={{ color: NAVY }}>
              Turn fraud awareness into member action
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600 sm:text-xl">
              Give service members, veterans and their families a practical scam-education resource
              from your credit union. OnGuard brings lessons, real-life scenarios and guidance
              together under your brand.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#demo" onClick={() => trackDcuc('pilot_click', 'hero')} className="dl-button dl-button-primary">
                Request a branded pilot<ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <button type="button" onClick={() => setChatOpen(true)} className="dl-button dl-button-secondary">Ask a question</button>
            </div>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">One link to share. No member account or app required.</p>
          </div>
          <figure className="min-w-0">
            <img src="/dcuc/onguard-devices.webp" alt="OnGuard shown on a desktop, tablet and phone, as featured in the DCUC ad" width={1040} height={820} fetchPriority="high" className="h-auto w-full" />
            <figcaption className="mt-2 text-center text-sm text-slate-500">The OnGuard resource, customized for your credit union.</figcaption>
          </figure>
        </section>

        <section id="benefits" className="border-y border-slate-200 bg-slate-50 py-12 sm:py-16" aria-labelledby="dl-fit">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="max-w-3xl">
              <h2 id="dl-fit" className="dl-display text-balance text-3xl font-bold leading-tight sm:text-4xl" style={{ color: NAVY }}>Make your members harder to scam</h2>
              <p className="mt-4 max-w-2xl text-lg leading-relaxed text-slate-600">Help members spot red flags, pause under pressure and know where to turn. OnGuard complements the alerts, workshops and fraud education your credit union already provides.</p>
            </div>
            <ul className="mt-7 grid gap-5 md:grid-cols-3 md:gap-8">
              {WHY.map(({ title, body }) => (
                <li key={title} className="border-t border-slate-300 pt-4">
                  <h3 className="flex items-start gap-2 text-lg font-semibold leading-snug" style={{ color: NAVY }}>
                    <Check className="mt-1 h-[18px] w-[18px] shrink-0" style={{ color: RED }} aria-hidden="true" />
                    <span>{title}</span>
                  </h3>
                  <p className="mt-2 pl-[26px] text-base leading-relaxed text-slate-600">{body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="members" className="py-14 sm:py-20" aria-labelledby="dl-members">
          <div className="mx-auto grid max-w-6xl gap-9 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <Eyebrow>What members get</Eyebrow>
              <h2 id="dl-members" className="dl-display mt-3 text-balance text-3xl font-bold leading-tight sm:text-4xl" style={{ color: NAVY }}>Practical education for military households</h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">Short lessons, realistic scenarios and practical guidance for recognizing scams and knowing what to do next.</p>
            </div>
            <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
              {MEMBER_FEATURES.map(({ title, body }) => (
                <div key={title}>
                  <h3 className="text-lg font-semibold leading-snug" style={{ color: NAVY }}>{title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-600">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="branding" className="pb-14 sm:pb-20" aria-labelledby="dl-branding">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="dl-card grid items-center gap-9 rounded-3xl p-6 text-white sm:p-10 lg:grid-cols-2 lg:gap-14">
              <div>
                <Eyebrow light>Your brand. Your member relationship.</Eyebrow>
                <h2 id="dl-branding" className="dl-display mt-3 text-balance text-3xl font-bold leading-tight sm:text-4xl">A DCUC member benefit you can make your own</h2>
                <p className="mt-5 text-lg leading-relaxed text-white/85">Your members see your credit union’s identity and know where to turn. Your approved contact details and reporting instructions become part of the experience.</p>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {CUSTOMIZE.map(item => <li key={item} className="flex items-center gap-2.5 text-base"><Check className="h-5 w-5 shrink-0 text-[#ff8a96]" aria-hidden="true" />{item}</li>)}
                </ul>
              </div>
              <figure className="min-w-0">
                <div className="overflow-hidden rounded-2xl bg-white text-slate-900">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">onguard.yourcu.com</div>
                  <div className="flex items-center justify-between gap-3 px-5 py-5">
                    <span className="rounded-md border-2 border-dashed border-slate-300 px-3 py-2 text-sm font-bold" style={{ color: NAVY }}>Your logo</span>
                    <span className="text-sm text-slate-500">Scam education</span>
                  </div>
                  <div className="mx-5 rounded-xl p-5 text-white" style={{ background: NAVY }}>
                    <p className="text-sm text-white/75">For your members and their families</p>
                    <p className="dl-display mt-2 text-2xl font-bold">Learn how to spot scams.</p>
                    <p className="mt-3 text-sm leading-relaxed text-white/80">Lessons · Scenarios · Practical resources</p>
                  </div>
                  <div className="m-5 border-t border-slate-200 pt-4">
                    <p className="text-base font-semibold" style={{ color: NAVY }}>Need to report a scam?</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">Your credit union’s approved guidance and contact details appear here.</p>
                    <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: RED }}><Phone className="h-4 w-4" aria-hidden="true" />Your fraud contact</span>
                  </div>
                </div>
                <figcaption className="mt-3 text-sm leading-relaxed text-white/70">Illustrative branded layout. Your details are agreed during setup.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section id="how" className="dl-anchor pb-14 sm:pb-20" aria-labelledby="dl-launch">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h2 id="dl-launch" className="dl-display text-3xl font-bold sm:text-4xl" style={{ color: NAVY }}>From a first look to a resource you can share</h2>
            <ol className="mt-8 grid gap-7 md:grid-cols-3 md:gap-9">
              {STEPS.map((step, i) => <li key={step.title} className="border-t border-slate-300 pt-5"><span className="text-sm font-semibold" style={{ color: RED }}>Step {i + 1}</span><h3 className="mt-2 text-xl font-semibold" style={{ color: NAVY }}>{step.title}</h3><p className="mt-2 text-base leading-relaxed text-slate-600">{step.body}</p></li>)}
            </ol>
          </div>
        </section>

        <section id="demo" tabIndex={-1} className="dl-anchor pb-14 sm:pb-20" aria-labelledby="dl-enquiry">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="dl-card grid items-start gap-8 rounded-3xl p-5 text-white sm:p-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
              <div className="pt-2">
                <h2 id="dl-enquiry" className="dl-display text-balance text-3xl font-bold leading-tight sm:text-4xl">Explore a branded pilot</h2>
                <p className="mt-4 text-lg leading-relaxed text-white/85">Tell us what you have in mind. The team can discuss a branded pilot and confirm the scope, pricing and next steps for your credit union.</p>
                <p className="mt-5 text-base leading-relaxed text-white/75">The team can walk you through the member experience, customization and pricing before you decide.</p>
                <div className="mt-7 border-t border-white/20 pt-6">
                  <p className="mb-4 text-base font-semibold">Prefer a conversation? Contact Lightbulb Press.</p>
                  <a href={DEMO_PHONE_HREF} onClick={() => trackDcuc('phone_click','contact')} className="inline-flex min-h-11 items-center gap-3 text-lg font-semibold text-white hover:underline"><Phone className="h-5 w-5 shrink-0" aria-hidden="true" />{DEMO_PHONE}</a>
                  <a href={`mailto:${DEMO_EMAIL}?subject=OnGuard%20for%20our%20credit%20union`} onClick={() => trackDcuc('email_click','contact')} className="mt-1 flex min-h-11 items-center gap-3 text-base text-white hover:underline"><Mail className="h-5 w-5 shrink-0" aria-hidden="true" /><span className="break-all">{DEMO_EMAIL}</span></a>
                </div>
              </div>
              <DemoRequestForm source="dcuc-landing" accent={RED} fallback={{ email: DEMO_EMAIL, phone: DEMO_PHONE, phoneHref: DEMO_PHONE_HREF }} />
            </div>
          </div>
        </section>

        <section id="questions" className="border-t border-slate-200 bg-slate-50 py-12 sm:py-16" aria-labelledby="dl-questions">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <h2 id="dl-questions" className="dl-display text-3xl font-bold" style={{ color: NAVY }}>A few practical questions</h2>
            <div className="mt-6 divide-y divide-slate-200">
              {FAQS.map(faq => <details key={faq.q} onToggle={e => { if (e.currentTarget.open) trackDcuc('faq_open','faq') }} className="group py-4"><summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 text-lg font-semibold [&::-webkit-details-marker]:hidden" style={{ color: NAVY }}>{faq.q}<ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition group-open:rotate-180" aria-hidden="true" /></summary><p className="mt-2 text-base leading-relaxed text-slate-600">{faq.a}</p></details>)}
            </div>
            <button type="button" onClick={() => { setChatOpen(true) }} className="mt-6 inline-flex min-h-11 items-center gap-2 font-semibold underline underline-offset-4" style={{ color: RED }}>Ask us about your credit union<ArrowRight className="h-4 w-4" aria-hidden="true" /></button>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <OnGuardLogo />
          <nav aria-label="Legal information" className="flex gap-6 text-sm text-slate-600"><a href="/privacy" className="inline-flex min-h-11 items-center hover:underline">Privacy</a><a href="/terms" className="inline-flex min-h-11 items-center hover:underline">Terms</a></nav>
        </div>
      </footer>
      <WebsiteChat open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  )
}
